import { useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Download, Upload, CheckCircle2 } from "lucide-react";

const KINDS = {
  schools: {
    title: "Schools & contacts",
    desc: "Columns: school, district, name, role, email. Schools matching an existing record (including directory-synced schools) are linked and updated — never duplicated, and directory-owned fields are never overwritten. Contacts are matched by email.",
  },
  consultants: {
    title: "Consultants",
    desc: "Columns: name, email, assigned_schools (school names separated by semicolons). New consultants are created as pending invitations — no email is sent by the import; send setup links from Settings → Team members.",
  },
};

function ImportPanel({ kind }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const cfg = KINDS[kind];

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${kind}/preview`, { text });
      setPreview(data);
      if (!data.rows.length) toast.error("No usable rows found.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const rows = preview.rows.filter((r) => r.include !== false && !(r.errors || []).length)
        .map((r) => (kind === "schools"
          ? { school: r.school, district: r.district, name: r.name, role: r.role, email: r.email }
          : { name: r.name, email: r.email, schools: r.schools_matched }));
      const { data } = await api.post(`/imports/${kind}/commit`, { rows });
      toast.success(JSON.stringify(data.note ? { ...data, note: undefined } : data));
      if (data.note) toast(data.note);
      setPreview(null); setText("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card p-6 space-y-4" data-testid={`import-panel-${kind}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif font-bold text-lg text-navy">{cfg.title}</h2>
          <p className="text-sm text-[#57534E] max-w-2xl">{cfg.desc}</p>
        </div>
        <a className="btn-ghost text-xs" href={`${process.env.REACT_APP_BACKEND_URL}/api/imports/template/${kind}`}
          download data-testid={`import-template-${kind}`}><Download size={13} /> CSV template</a>
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <label className="btn-outline text-xs cursor-pointer" data-testid={`import-file-label-${kind}`}>
          <Upload size={13} /> Choose CSV file
          <input type="file" accept=".csv,.tsv,.txt" className="hidden" onChange={onFile} data-testid={`import-file-${kind}`} />
        </label>
        <span className="text-xs text-[#78716C]">or paste below</span>
      </div>
      <textarea className="input font-mono text-xs min-h-[100px]" value={text} data-testid={`import-text-${kind}`}
        onChange={(e) => setText(e.target.value)} placeholder={kind === "schools"
          ? "school,district,name,role,email\n…" : "name,email,assigned_schools\n…"} />
      <div className="flex gap-2">
        <button className="btn-outline" onClick={runPreview} disabled={busy || !text.trim()} data-testid={`import-preview-${kind}`}>Preview import</button>
        {preview && <button className="btn-primary" onClick={commit} disabled={busy} data-testid={`import-commit-${kind}`}>
          <CheckCircle2 size={15} /> Import {preview.rows.filter((r) => r.include !== false && !(r.errors || []).length).length} rows</button>}
      </div>
      {preview && (
        <div className="overflow-x-auto" data-testid={`import-preview-table-${kind}`}>
          <table className="w-full">
            <thead className="bg-sand/60"><tr>
              <th className="table-th">Row</th>
              {kind === "schools"
                ? <><th className="table-th">School</th><th className="table-th">Contact</th><th className="table-th">Email</th><th className="table-th">School action</th><th className="table-th">Contact action</th></>
                : <><th className="table-th">Name</th><th className="table-th">Email</th><th className="table-th">Schools matched</th><th className="table-th">Unmatched</th><th className="table-th">User action</th></>}
              <th className="table-th">Issues</th>
            </tr></thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.row} className={(r.errors || []).length ? "bg-[#FEF2F2]" : ""} data-testid={`import-row-${kind}-${r.row}`}>
                  <td className="table-td text-xs">{r.row}</td>
                  {kind === "schools" ? (
                    <>
                      <td className="table-td font-bold">{r.school}</td>
                      <td className="table-td">{r.name} <span className="text-xs text-[#78716C]">{r.role}</span></td>
                      <td className="table-td text-xs">{r.email}</td>
                      <td className="table-td text-xs">{r.school_action}</td>
                      <td className="table-td text-xs">{r.contact_action}</td>
                    </>
                  ) : (
                    <>
                      <td className="table-td font-bold">{r.name}</td>
                      <td className="table-td text-xs">{r.email}</td>
                      <td className="table-td text-xs">{(r.schools_matched || []).join("; ") || "—"}</td>
                      <td className="table-td text-xs text-[#991B1B]">{(r.schools_unmatched || []).join("; ") || "—"}</td>
                      <td className="table-td text-xs">{r.user_action}</td>
                    </>
                  )}
                  <td className="table-td text-xs text-[#991B1B]">{(r.errors || []).join("; ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Imports() {
  return (
    <div className="space-y-6 max-w-6xl" data-testid="imports-page">
      <div>
        <p className="eyebrow">Admin</p>
        <h1 className="section-title text-3xl mt-1">Bulk import</h1>
        <p className="text-sm text-[#57534E] mt-1">Import schools/contacts and consultants from a spreadsheet saved as CSV.
          Every import shows a preview first — nothing is written until you confirm.</p>
      </div>
      <ImportPanel kind="schools" />
      <ImportPanel kind="consultants" />
    </div>
  );
}
